import { EventAPI } from '@praxis/analytics'
import type { EventAPIOptions } from '@praxis/analytics/types'
import apiConfig from '../config/apiConfig'
import {
  getExternalVendorDetails,
  getFromLocalStorage,
  getSelectedAccount,
  getUserEmail,
  getUserName,
} from './helper'
import { isLoggedInAsInternalUser } from './userAccessHelpers'
import { trackUserEvent } from './otel'

let analyticsClient: EventAPI | null = null

const createAnalyticsClient = () => {
  const options: EventAPIOptions = {
    appName: apiConfig.fireflyConfig.appName,
    apiKey: apiConfig.fireflyConfig.apiKey,
    url: apiConfig.fireflyConfig.url,
    retries: apiConfig.fireflyConfig.retries,
  }

  analyticsClient = new EventAPI(options)
}

// This initializes the Praxis analytics EventAPI client.
const getAnalyticsClient = () => {
  if (!analyticsClient) {
    createAnalyticsClient()
  }
  return analyticsClient as EventAPI
}

// Test-only helpers to control the internal client.
const __setAnalyticsClientForTest = (client: EventAPI | null) => {
  analyticsClient = client
}
const __resetAnalyticsClientForTest = () => {
  analyticsClient = null
}

const buildViewObject = (eventType: string, viewData: any = {}) => {
  const view: any = {}
  const { lanId } = getFromLocalStorage('userDir') || {}
  const vendorDetails = getSelectedAccount() || getExternalVendorDetails()
  view.additionalInfo = { payload: { ...viewData } }
  view.additionalInfo.lanId = lanId
  view.additionalInfo.vendor = vendorDetails?.value
  view.additionalInfo.vendorId = vendorDetails?.id
  view.additionalInfo.userName = getUserName()
  view.additionalInfo.userEmail = getUserEmail()
  view.additionalInfo.dateTime = new Date().toString()
  view.additionalInfo.type = eventType
  view.additionalInfo.userType = isLoggedInAsInternalUser()
    ? 'internal'
    : 'external'
  view.globalName = window.location.pathname?.toString()
  view.name = document.title?.toString()

  return view
}

// Public API used throughout the app; now delegates to Praxis analytics.
const trackEvent = (eventType: string, viewData: any = {}) => {
  const client = getAnalyticsClient()
  const view = buildViewObject(eventType, viewData)

  // Emit an OTel counter alongside the Firefly analytics event so both
  // pipelines stay in sync. Safe no-op until OTel is initialized.
  trackUserEvent(eventType)

  // Use a click-style event and place the original eventType in the event value
  // add `view` alongside the BaseEvent fields.
  if (eventType === 'Logon') {
    client.sendEvent({
      event: { type: 'LOGIN', value: JSON.stringify(view) },
    })
  } else {
    client.sendEvent({
      event: { type: 'CLICK', value: JSON.stringify(view) },
    })
  }
  return
}

export {
  trackEvent,
  __setAnalyticsClientForTest,
  __resetAnalyticsClientForTest,
}
